import type {
  SavedSearchGroupItem,
  SavedSearchItem,
} from '@iconik-io/iconik-api/src/schema_patched/saved_search';
import type { AssetsCustomActionSchemaWithId } from '@iconik-io/iconik-api/src/services/assets';
import {
  httpDeleteFavorites,
  httpPostFavorites,
} from '@iconik-io/iconik-api/src/services/search';
import {
  OBJECT_TYPE_SAVED_SEARCH_GROUPS,
  OBJECT_TYPE_SAVED_SEARCHES,
} from '@iconik-io/iconik-api/src/types/object_types';
import type { UserRoles } from '@iconik-io/shared-lib/src/utils/roles';
import { customActionCallback } from '@iconik-io/web/src/ducks/custom_actions/thunks';

import ContextMenu from '../../components/context_menu/context_menu';
import contextMenuConnector from '../../components/context_menu/context_menu_connector';
import type {
  ContextMenuItemDefinition,
  GetDefinition,
} from '../../components/context_menu/types';
import { options } from '../../components/search_items/context_menu_options';
import {
  ADD_TO_COLLECTION_ADVANCED_MODAL,
  ANALYZE_CONFIRM_MODAL,
  DELETE_BULK_MODAL,
  DELETE_FILES_FROM_STORAGE_MODAL,
  FACE_RECOGNITION_TRIGGER_CONFIRM_MODAL,
  METADATA_MODAL,
  NEW_COLLECTION_MODAL,
  REINDEX_BULK_MODAL,
  RETRANSCODE_CONFIRM_MODAL,
  SAVED_SEARCH_ACLS_MODAL,
  SAVED_SEARCH_CHANGE_GROUP_MODAL,
  SAVED_SEARCH_CLEAR_GROUP_MODAL,
  SAVED_SEARCH_DELETE_MODAL,
  SAVED_SEARCH_EDIT_TITLE_MODAL,
  SAVED_SEARCH_GROUP_DELETE_MODAL,
  SAVED_SEARCH_GROUP_EDIT_TITLE_MODAL,
  SAVED_SEARCH_INFO_MODAL,
  SAVED_SEARCH_UPDATE_VALUES_MODAL,
  SAVED_SEARCHES_PANEL_ARCHIVE_MODAL,
  SAVED_SEARCHES_PANEL_RESTORE_MODAL,
  SAVED_SEARCHES_PANEL_TRANSFER_MODAL,
  TRANSCRIBE_CONFIRM_MODAL,
} from '../../constants/modals';
import {
  CONTEXT_MENU_PANELS_SAVED_SEARCHES,
  connectContextMenuConsumer,
} from '../../contexts/context_menu';
import { segmentTrackEvent } from '../../ducks/analytics_tracking/thunks';
import { cancellable } from '../../ducks/http/actions';
import { errorMessage } from '../../ducks/messages/thunks';
import { openModal } from '../../ducks/modals_manager/slice';
import type { CategoryObjectType } from '../../ducks/pages/admin_metadata_categories/types';
import { updateSavedSearchSuccess } from '../../ducks/saved_search/actions';
import { getIsAiMetadataEnrichmentEnabled } from '../../ducks/settings/select_features';
import { ICONS } from '../../icons/icon';
import type { AppDispatch } from '../../store/types';
import { allGrantedPermissions } from '../../types/common';
import permissionsArrayToObject from '../../utils/permissions_array_to_object';
import separateMenuGroups from '../../utils/separate_menu_groups';

function getSavedSearchOptions(
  userRoles: UserRoles,
  customSavedSearchActions: AssetsCustomActionSchemaWithId[],
  isAdmin: boolean,
  isFaceRecognitionEnabled: boolean,
  isAiMetadataEnabled: boolean,
  contextItems: SavedSearchItem[],
): ContextMenuItemDefinition[] {
  const items: ContextMenuItemDefinition[] = [];
  const [contextItem] = contextItems;

  const permissions = isAdmin
    ? allGrantedPermissions
    : permissionsArrayToObject(contextItem.permissions);

  const contentSubItems: ContextMenuItemDefinition[] = [];

  if (userRoles.canWriteMetadataValues) {
    contentSubItems.push({
      label: 'Bulk Metadata update…',
      action: () => (dispatch: AppDispatch) => {
        const objectType: CategoryObjectType = 'assets';
        dispatch(
          openModal({
            id: METADATA_MODAL,
            contextItems,
            options: {
              containerObjectType: OBJECT_TYPE_SAVED_SEARCHES,
              objectType,
            },
          }),
        );
      },
      icon: ICONS.Metadata,
    });
    if (isAiMetadataEnabled && userRoles.canTriggerMetadataAiFilling) {
      contentSubItems.push(
        options.AI_METADATA_BULK(
          contextItems,
          'saved_searches',
          'saved search menu',
        ),
      );
    }
  }

  if (userRoles.canWriteCollections && userRoles.canCreateRootCollections) {
    contentSubItems.push({
      label: 'Create collection…',
      action: () => (dispatch: AppDispatch) =>
        dispatch(
          openModal({
            id: NEW_COLLECTION_MODAL,
            contextItems: undefined,
            options: {
              title: contextItem.name,
              savedSearchItem: contextItem,
            },
          }),
        ),
      icon: ICONS.Collection,
    });
  }

  if (userRoles.canWriteCollections) {
    contentSubItems.push({
      label: 'Add to collection…',
      action: () => (dispatch: AppDispatch) =>
        dispatch(
          openModal({
            id: ADD_TO_COLLECTION_ADVANCED_MODAL,
            contextItems: undefined,
            options: {
              savedSearchItem: contextItem,
            },
          }),
        ),
      icon: ICONS.Collection,
    });
  }

  const reviewSubitems = [
    options.ENTITY_APPROVE(contextItems, OBJECT_TYPE_SAVED_SEARCHES, false),
    options.ENTITY_REJECT(contextItems, OBJECT_TYPE_SAVED_SEARCHES, false),
    options.ENTITY_RESET(contextItems, OBJECT_TYPE_SAVED_SEARCHES, false),
  ];

  if (
    userRoles.canApproveWithoutRequest &&
    userRoles.canWriteApprovalStatusInBulk
  ) {
    contentSubItems.push(options.ENTITY_REVIEW(reviewSubitems, false));
  }

  if (userRoles.canWriteTransfers) {
    contentSubItems.push({
      label: 'Transfer/Copy…',
      action: () => (dispatch: AppDispatch) =>
        dispatch(
          openModal({
            id: SAVED_SEARCHES_PANEL_TRANSFER_MODAL,
            contextItems,
            options: {
              containerObjectType: OBJECT_TYPE_SAVED_SEARCHES,
            },
          }),
        ),
      icon: ICONS.Transfer,
      multiple: true,
    });
  }

  if (userRoles.canArchiveFormats) {
    contentSubItems.push({
      label: 'Archive…',
      action: () => (dispatch: AppDispatch) =>
        dispatch(
          openModal({
            id: SAVED_SEARCHES_PANEL_ARCHIVE_MODAL,
            contextItems,
            options: {
              containerObjectType: OBJECT_TYPE_SAVED_SEARCHES,
            },
          }),
        ),
      icon: ICONS.Archive,
      multiple: true,
    });
  }

  if (userRoles.canReindexAssets) {
    contentSubItems.push({
      label: 'Reindex…',
      action: () => (dispatch: AppDispatch) =>
        dispatch(
          openModal({
            id: REINDEX_BULK_MODAL,
            contextItems,
          }),
        ),
      icon: ICONS.Index,
    });
  }

  if (userRoles.canRestoreArchivedFormats) {
    contentSubItems.push({
      label: 'Restore…',
      action: () => (dispatch: AppDispatch) =>
        dispatch(
          openModal({
            id: SAVED_SEARCHES_PANEL_RESTORE_MODAL,
            contextItems,
            options: {
              containerObjectType: OBJECT_TYPE_SAVED_SEARCHES,
            },
          }),
        ),
      icon: ICONS.Restore,
      multiple: true,
    });
  }

  if (userRoles.canCreateTranscodeJobs) {
    contentSubItems.push({
      label: 'Re-transcode…',
      action: () => (dispatch: AppDispatch) =>
        dispatch(
          openModal({
            id: RETRANSCODE_CONFIRM_MODAL,
            contextItems,
            options: {
              containerObjectType: OBJECT_TYPE_SAVED_SEARCHES,
            },
          }),
        ),
      icon: ICONS.Transcoder,
      multiple: true,
    });
  }

  if (userRoles.canAnalyzeContent) {
    contentSubItems.push({
      label: 'Analyze…',
      action: () => (dispatch: AppDispatch) =>
        dispatch(
          openModal({
            id: ANALYZE_CONFIRM_MODAL,
            contextItems,
            options: {
              containerObjectType: OBJECT_TYPE_SAVED_SEARCHES,
            },
          }),
        ),
      icon: ICONS.Analyze,
      multiple: true,
    });
  }

  if (isFaceRecognitionEnabled && userRoles.canTriggerFaceRecognition) {
    contentSubItems.push({
      label: 'Run face recognition…',
      action: () => (dispatch: AppDispatch) =>
        dispatch(
          openModal({
            id: FACE_RECOGNITION_TRIGGER_CONFIRM_MODAL,
            contextItems,
            options: {
              containerObjectType: OBJECT_TYPE_SAVED_SEARCHES,
            },
          }),
        ),
      icon: ICONS.FaceRecognition,
      disabled: false,
      multiple: true,
    });
  }

  if (userRoles.canTranscribeContent) {
    contentSubItems.push({
      label: 'Transcribe…',
      action: () => (dispatch: AppDispatch) =>
        dispatch(
          openModal({
            id: TRANSCRIBE_CONFIRM_MODAL,
            contextItems,
            options: {
              containerObjectType: OBJECT_TYPE_SAVED_SEARCHES,
            },
          }),
        ),
      icon: ICONS.Transcription,
    });
  }

  const customActionItemsGroup: ContextMenuItemDefinition[] = [];

  for (const customAction of customSavedSearchActions) {
    customActionItemsGroup.push({
      label: customAction.title,
      action: () => {
        const data = { saved_search_ids: [contextItem.id] };
        return (dispatch: AppDispatch) =>
          dispatch(
            customActionCallback(
              {
                label: customAction.title,
                id: customAction.id,
                context: customAction.context,
                type: customAction.type,
                metadataView: customAction.metadata_view,
              },
              data,
            ),
          );
      },
    });
  }

  const deleteItemsGroup: ContextMenuItemDefinition[] = [];

  if (userRoles.canDeleteSavedSearches && permissions.delete) {
    deleteItemsGroup.push({
      label: 'Delete…',
      action: () => (dispatch: AppDispatch) =>
        dispatch(
          openModal({
            id: SAVED_SEARCH_DELETE_MODAL,
            contextItems,
          }),
        ),
      icon: ICONS.TrashBin,
      multiple: true,
    });
  }

  const contentDeleteGroup = [];
  if (userRoles.canDeleteFiles) {
    contentDeleteGroup.push({
      label: 'Delete files from storage…',
      icon: ICONS.TrashBin,

      action: () => (dispatch: AppDispatch) =>
        dispatch(
          openModal({ id: DELETE_FILES_FROM_STORAGE_MODAL, contextItems }),
        ),
    });
  }

  if (userRoles.canDeleteAssets) {
    contentDeleteGroup.push({
      label: 'Delete…',
      action: () => (dispatch: AppDispatch) =>
        dispatch(
          openModal({
            id: DELETE_BULK_MODAL,
            contextItems,
          }),
        ),
      icon: ICONS.TrashBin,
      multiple: true,
    });
  }
  const contentOptions = separateMenuGroups(
    [contentSubItems, customActionItemsGroup, contentDeleteGroup],
    contextItems.length,
  );

  if (contentOptions.length) {
    items.push({
      label: 'Edit Content',
      subitems: contentOptions,
      icon: ICONS.Edit,
    });
  }

  if (permissions['change-acl'] && userRoles.canWriteAcls) {
    items.push({
      label: 'Access Control…',
      action: () => (dispatch: AppDispatch) =>
        dispatch(
          openModal({
            id: SAVED_SEARCH_ACLS_MODAL,
            contextItems,
          }),
        ),
      icon: ICONS.Permission,
    });
  }

  if (permissions.write && userRoles.canWriteSavedSearches) {
    if (userRoles.canWriteSavedSearches && permissions.write) {
      items.push({
        label: 'Edit Title…',
        action: () => (dispatch: AppDispatch) =>
          dispatch(
            openModal({ id: SAVED_SEARCH_EDIT_TITLE_MODAL, contextItems }),
          ),
        icon: ICONS.Edit,
      });
    }

    items.push({
      label: contextItem.group_id ? 'Change Group…' : 'Add to Group…',
      action: () => (dispatch: AppDispatch) =>
        dispatch(
          openModal({
            id: SAVED_SEARCH_CHANGE_GROUP_MODAL,
            contextItems,
          }),
        ),
      icon: ICONS.Copy,
      multiple: true,
    });

    if (contextItem.group_id) {
      items.push({
        label: 'Remove from Group…',
        action: () => (dispatch: AppDispatch) =>
          dispatch(
            openModal({
              id: SAVED_SEARCH_CLEAR_GROUP_MODAL,
              contextItems,
            }),
          ),
        icon: ICONS.RemoveContent,
        multiple: true,
      });
    }

    items.push({
      label: 'Update Values…',
      action: () => (dispatch: AppDispatch) =>
        dispatch(
          openModal({
            id: SAVED_SEARCH_UPDATE_VALUES_MODAL,
            contextItems,
          }),
        ),
      icon: ICONS.Refresh,
    });
  }

  if (userRoles.canReadSavedSearches) {
    items.push({
      label: 'Get Info…',
      action: () => (dispatch: AppDispatch) =>
        dispatch(
          openModal({
            id: SAVED_SEARCH_INFO_MODAL,
            contextItems,
          }),
        ),
      icon: ICONS.Info,
    });
  }

  if (userRoles.canWriteFavorites) {
    items.push({
      label: contextItem.favoured
        ? 'Remove from favorites'
        : 'Add to favorites',
      action: () => (dispatch: AppDispatch) => {
        if (contextItem.favoured) {
          dispatch(
            cancellable(
              httpDeleteFavorites(
                {
                  object_ids: [contextItem.id],
                },
                { index_immediately: true },
              ),
              `favourite_${contextItem.id}`,
            ),
          )
            .then(() => {
              const newSavedSearch = { ...contextItem, favoured: false };
              dispatch(updateSavedSearchSuccess(newSavedSearch));

              dispatch(
                segmentTrackEvent({
                  eventName: `Removed From Favorites`,
                  properties: {
                    'Entity Type': OBJECT_TYPE_SAVED_SEARCHES,
                  },
                }),
              );
            })
            .catch(() => {
              dispatch(
                errorMessage({
                  text: `Failed to remove ${contextItem.name} from favorites`,
                }),
              );
            });

          return;
        }

        dispatch(
          httpPostFavorites(
            {
              object_ids: [contextItem.id],
            },
            { index_immediately: true },
          ),
        )
          .then(() => {
            const newSavedSearch = { ...contextItem, favoured: true };
            dispatch(updateSavedSearchSuccess(newSavedSearch));

            dispatch(
              segmentTrackEvent({
                eventName: `Added To Favorites`,
                properties: {
                  'Entity Type': OBJECT_TYPE_SAVED_SEARCHES,
                },
              }),
            );
          })
          .catch(() => {
            dispatch(
              errorMessage({
                text: `Failed to add ${contextItem.name} to favorites`,
              }),
            );
          });
      },
      icon: contextItem.favoured ? ICONS.FavouriteFilled : ICONS.Favourite,
    });
  }

  return separateMenuGroups([items, deleteItemsGroup], contextItems.length);
}

function getSavedSearchGroupOptions(
  userRoles: UserRoles,
  contextItems: SavedSearchItem[],
) {
  const items: ContextMenuItemDefinition[] = [];

  if (userRoles.canWriteSavedSearchGroups) {
    items.push({
      label: 'Edit Title…',
      action: () => (dispatch: AppDispatch) =>
        dispatch(
          openModal({
            id: SAVED_SEARCH_GROUP_EDIT_TITLE_MODAL,
            contextItems,
          }),
        ),
      icon: ICONS.Edit,
    });
  }

  if (userRoles.canDeleteSavedSearchGroups) {
    items.push({
      label: 'Delete…',
      action: () => (dispatch: AppDispatch) =>
        dispatch(
          openModal({
            id: SAVED_SEARCH_GROUP_DELETE_MODAL,
            contextItems,
          }),
        ),
      icon: ICONS.TrashBin,
      multiple: true,
    });
  }

  return items;
}

export const getDefinition: GetDefinition<
  SavedSearchItem | SavedSearchGroupItem
> = (props, state) => {
  const contextItems = props.contextItems;
  const { userRoles } = state.rolesReducer;
  const isAdmin = Boolean(state.authReducer.userData.is_admin);
  const isFaceRecognitionEnabled = Boolean(
    state.settings.merged.enable_face_recognition,
  );
  const isAiMetadataEnabled = getIsAiMetadataEnrichmentEnabled(state);
  const customActions = state.customActions.customActionsContexts ?? {};
  const customSavedSearchActions = customActions.SAVED_SEARCH ?? [];

  let items: ContextMenuItemDefinition[] = [];

  if (contextItems.length === 1) {
    const [contextItem] = contextItems;
    items =
      contextItem.object_type === OBJECT_TYPE_SAVED_SEARCH_GROUPS
        ? getSavedSearchGroupOptions(userRoles, contextItems)
        : getSavedSearchOptions(
            userRoles,
            customSavedSearchActions,
            isAdmin,
            isFaceRecognitionEnabled,
            isAiMetadataEnabled,
            contextItems,
          );
  } else if (contextItems.length > 1) {
    const [firstItem] = contextItems;
    const savedSearchesOnly = contextItems.every(
      (item) => item.object_type === OBJECT_TYPE_SAVED_SEARCHES,
    );
    const sameGroup = contextItems.every(
      (item) =>
        'group_id' in item &&
        'group_id' in firstItem &&
        item.group_id === firstItem.group_id,
    );
    if (savedSearchesOnly && sameGroup) {
      items = getSavedSearchOptions(
        userRoles,
        customSavedSearchActions,
        isAdmin,
        isFaceRecognitionEnabled,
        isAiMetadataEnabled,
        contextItems,
      );
    }

    const savedSearchGroupsOnly = contextItems.every(
      (item) => item.object_type === OBJECT_TYPE_SAVED_SEARCH_GROUPS,
    );
    if (savedSearchGroupsOnly) {
      items = getSavedSearchGroupOptions(userRoles, contextItems);
    }
  }

  return {
    items,
  };
};

const SavedSearchContextMenu = connectContextMenuConsumer(
  contextMenuConnector(getDefinition)(ContextMenu),
  CONTEXT_MENU_PANELS_SAVED_SEARCHES,
);

export default SavedSearchContextMenu;
